import PropTypes from "prop-types";
import { Card, Divider } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
    calculateAddonAmount,
    calculateAccommodationAmount,
    getOfferWindow,
    getHSSWindow,
    calculateHssSurcharge,
} from "./hardcodedData";
import { formatAmount } from "./TravelOrderUtility";

const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

function OrderSummaryPanel({
                               selectedTripPackage,
                               travellers,
                               selectedDepartureDate,
                               selectedAccommodations,
                               accommodationTravelerMap,
                               selectedAddons,
                               addonTravelerMap,
                               tripNights,
                               pricing,
                               hssTemplate,
                               taxes,
                           }) {
    if (!selectedTripPackage) {
        return (
            <Card sx={{ borderRadius: 2, boxShadow: "0 2px 12px rgba(14,35,72,0.08)", position: "sticky", top: 88 }}>
                <MDBox p={2.5}>
                    <MDTypography variant="h6" fontWeight="bold" mb={1}>
                        Order Summary
                    </MDTypography>
                    <MDTypography variant="body2" color="text">
                        Select a trip package to view order summary.
                    </MDTypography>
                </MDBox>
            </Card>
        );
    }

    const packageCurrency = selectedTripPackage.currency || "INR";
    // Multiple trips can be bundled into one package — join their countries
    // rather than assuming a single primary_country like the old trip object did.
    const destinationLabel = (selectedTripPackage.destinations || []).filter(Boolean).join(", ");

    // Base package price × travelers — matches CreateTravelOrder's reviewPricing.baseSubtotal input
    const basePrice = selectedTripPackage.base_price * travellers;

    const offerWindow = getOfferWindow(selectedDepartureDate);
    const rawDiscountPercentage = selectedDepartureDate?.discount_percentage || 0;
    const isDiscountLive = rawDiscountPercentage > 0 && (!offerWindow || offerWindow.isActive);
    const discountPercentage = isDiscountLive ? rawDiscountPercentage : 0;
    const discountedPrice = basePrice - (basePrice * discountPercentage) / 100;

    const hssWindow = getHSSWindow(hssTemplate);
    const isHssLive = Boolean(hssTemplate) && Boolean(hssWindow?.isActive);
    const hssSurcharge = calculateHssSurcharge(discountedPrice, hssTemplate, selectedDepartureDate);
    const priceAfterHss = discountedPrice + hssSurcharge;

    const getDiscountLabel = (offerWindow) => {
        switch (offerWindow?.type) {
            case "early_bird":
                return "Early Bird Discount";
            case "last_minute":
                return "Last Minute Discount";
            default:
                return "Discount"; // generic/group deal, or no offerWindow at all
        }
    };

    return (
        <Card sx={{ borderRadius: 2, boxShadow: "0 2px 12px rgba(14,35,72,0.08)", position: "sticky", top: 88 }}>
            <MDBox p={2.5}>
                <MDTypography variant="h6" fontWeight="bold" mb={2}>
                    Order Summary
                </MDTypography>

                <MDBox display="flex" gap={1.5} mb={2}>
                    <MDBox
                        component="img"
                        src={selectedTripPackage.image}
                        alt={selectedTripPackage.title}
                        sx={{ width: 72, height: 56, borderRadius: 1.5, objectFit: "cover", bgcolor: "#F0F3F8" }}
                    />
                    <MDBox>
                        <MDTypography variant="button" fontWeight="bold" display="block">
                            {selectedTripPackage.title}
                        </MDTypography>
                        {destinationLabel && (
                            <MDTypography variant="caption" display="block">
                                {destinationLabel.toUpperCase()}
                            </MDTypography>
                        )}
                        <MDTypography variant="caption" color="text">
                            {selectedDepartureDate &&
                                `${formatDate(selectedDepartureDate?.departure_date)} – ${formatDate(selectedDepartureDate?.return_date)}`}
                        </MDTypography>
                    </MDBox>
                </MDBox>

                <Divider sx={{ my: 1.5 }} />

                <SummaryRow label="Travelers" value={`${travellers} Traveler${travellers > 1 ? "s" : ""}`} />
                <SummaryRow label="Trip Duration" value={selectedTripPackage.duration} />
                <SummaryRow label="Base Price" value={`${formatAmount(selectedTripPackage.base_price, packageCurrency)} × ${travellers}`} />

                {discountPercentage > 0 && (
                    <SummaryRow
                        label={`${getDiscountLabel(offerWindow)} (${discountPercentage}%)`}
                        value={`- ${formatAmount(basePrice - discountedPrice, packageCurrency)}`}
                    />
                )}

                {isHssLive && (
                    <SummaryRow
                        label={`High Season Surcharge (${hssTemplate.hss_percentage}%)`}
                        value={`+ ${formatAmount(hssSurcharge, packageCurrency)}`}
                    />
                )}

                <SummaryRow
                    label={isHssLive ? "Price after Surcharge" : discountPercentage > 0 ? "Discounted Price" : "Subtotal"}
                    value={formatAmount(priceAfterHss, packageCurrency)}
                    bold
                />
                <Divider sx={{ my: 1.5 }} />
                <MDTypography variant="button" fontWeight="bold" sx={{ my: 1.5 }}>
                    Price Breakdown
                </MDTypography>
                <SummaryRow label="Subtotal" value={formatAmount(priceAfterHss, packageCurrency)} />

                {/* One line per selected accommodation, priced by its own assigned traveler count */}

                {selectedAccommodations.map((item) => {
                    const roomTravelerCount = (accommodationTravelerMap[item.addon.id] || []).length;
                    return (
                        <SummaryRow
                            key={item.addon.id}
                            label={`Accommodation (${item.linkedService.name})`}
                            value={formatAmount(calculateAccommodationAmount(item, tripNights, roomTravelerCount), packageCurrency)}
                        />
                    );
                })}

                {/* One line per selected addon, priced by its own assigned traveler count */}

                {selectedAddons.map((item) => {
                    const addonTravelerCount = (addonTravelerMap[item.addon.id] || []).length;
                    return (
                        <SummaryRow
                            key={item.addon.id}
                            label={item.addon.name}
                            value={formatAmount(calculateAddonAmount(item.addon, addonTravelerCount), packageCurrency)}
                        />
                    );
                })}

                {
                    taxes.map((item) => (
                        <SummaryRow
                            key={item.id || item.tax_type}
                            label={`${item.tax_type} (${item.rate}%)`}
                            value={formatAmount(pricing.subtotal * (item.rate / 100), packageCurrency)}
                        />
                    ))
                }

                {selectedDepartureDate && (
                    <>
                        <Divider sx={{ my: 1.5 }} />
                        <SummaryRow label="Departure City" value={selectedDepartureDate?.departure_city} />
                    </>
                )}

                <Divider sx={{ my: 2 }} />

                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                    <MDTypography variant="button" fontWeight="bold">
                        Total Amount
                    </MDTypography>
                    <MDTypography variant="h5" color="info" fontWeight="bold">
                        {formatAmount(pricing.total, packageCurrency)}
                    </MDTypography>
                </MDBox>
                {pricing.isHssLive && pricing.hssSurchargeAmount > 0 && (
                    <MDTypography variant="caption" color="warning" display="block" sx={{ mb: 0.5 }}>
                        Includes {formatAmount(pricing.hssSurchargeAmount, packageCurrency)} High Season Surcharge
                    </MDTypography>
                )}
                <Divider sx={{ my: 1 }} />
            </MDBox>
        </Card>
    );
}

function SummaryRow({ label, value, bold, small }) {
    return (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" py={small ? 0.35 : 0.6}>
            <MDTypography variant={small ? "caption" : "button"} color="text" fontWeight={bold ? "bold" : "regular"}>
                {label}
            </MDTypography>
            <MDTypography variant={small ? "caption" : "button"} fontWeight={bold ? "bold" : "medium"}>
                {value}
            </MDTypography>
        </MDBox>
    );
}

OrderSummaryPanel.propTypes = {
    selectedTripPackage: PropTypes.object,
    travellers: PropTypes.number,
    selectedDepartureDate: PropTypes.object,
    selectedAccommodations: PropTypes.arrayOf(PropTypes.object),
    accommodationTravelerMap: PropTypes.object,
    selectedAddons: PropTypes.arrayOf(PropTypes.object),
    addonTravelerMap: PropTypes.object,
    tripNights: PropTypes.number,
    pricing: PropTypes.shape({
        subtotal: PropTypes.number,
        total: PropTypes.number,
        hssTemplate: PropTypes.object,
        taxes: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
};

SummaryRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    bold: PropTypes.bool,
    small: PropTypes.bool,
};

export default OrderSummaryPanel;